"use client";

import React from "react";
import Link from "next/link";
import { SectionHeader, EntityDisplay, CTAButton } from "fansunited-sports-ui";
import { HorizontalScroll } from "@/components/ui/HorizontalScroll";
import { useLabels } from "@/lib/labels/LabelsProvider";
import { useNavigation } from "@/lib/navigation/NavigationProvider";
import { TeamLogo } from "@/components/ui/TeamLogo";
import type { FUSportsStandingsEntryWithNativeTeamId } from "@/utilities/data/types";

export interface StandingsHorizontalProps {
    /** Standings entries with team data */
    standings: FUSportsStandingsEntryWithNativeTeamId[];
    /** Show matches played below team name */
    showPlayed?: boolean;
}

/**
 * TeamCard - Individual team card with rank badge overlay
 */
const TeamCard: React.FC<{
    rank: number;
    name: string;
    shortName?: string;
    logo?: string;
    points: number;
    href: string;
    played?: number;
    showPlayed?: boolean;
    pointsLabel?: string;
    playedLabel?: string;
}> = ({
    rank,
    name,
    shortName,
    logo,
    points,
    href,
    played,
    showPlayed,
    pointsLabel = "Pts",
    playedLabel = "P",
}) => {
    return (
        <Link
            prefetch={false}
            href={href}
            className="shrink-0 hover:opacity-80 transition-opacity"
            title={`${rank}. ${name} - ${points} ${pointsLabel}`}
        >
            <div className="bg-card text-card-foreground p-4 flex flex-col min-w-32">
                {/* Rank badge positioned on top-left of image */}
                <div className="flex justify-center mb-2">
                    <span className="w-6 h-6 bg-primary text-primary-foreground text-xs font-bold rounded-full flex items-center justify-center shadow-md z-10">
                        {rank}
                    </span>
                </div>
                <EntityDisplay
                    name={shortName || name.substring(0, 3).toUpperCase()}
                    image={<TeamLogo src={logo} size="xl" />}
                    imagePosition="top"
                    styles={{
                        imageSize: "2xl",
                        imageBorderRadius: "md",
                        typography: {
                            name: "text-xs opacity-70 text-center",
                        },
                    }}
                />
                <div className="flex flex-col items-center gap-0.5 mt-1">
                    <span className="text-[10px] font-semibold">
                        {points} {pointsLabel}
                    </span>
                    {showPlayed && played !== undefined && (
                        <span className="text-[10px] opacity-70">
                            {played} {playedLabel}
                        </span>
                    )}
                </div>
            </div>
        </Link>
    );
};

/**
 * StandingsHorizontal - Horizontal scrollable standings bar showing teams with stacked EntityDisplay.
 * Shows teams in ranking order with position badges.
 */
export const StandingsHorizontal: React.FC<StandingsHorizontalProps> = ({ standings, showPlayed = false }) => {
    const labels = useLabels();
    const nav = useNavigation();

    if (standings.length === 0) return null;

    // Sort by rank
    const sortedStandings = [...standings].sort((a, b) => a.rank - b.rank);

    return (
        <section>
            <SectionHeader
                entity={{
                    name: labels.homePage.standings.toUpperCase(),
                    styles: {
                        typography: {
                            name: "text-sm uppercase font-bold text-foreground tracking-wider",
                        },
                    },
                }}
                primaryCta={
                    <CTAButton
                        text={labels.homePage.fullTable}
                        href={nav.standings()}
                        variant="custom"
                        styles={{
                            container: "text-sm font-medium text-foreground hover:text-foreground/80 border-none p-0",
                        }}
                    />
                }
                styles={{ container: "mb-4" }}
            />
            <HorizontalScroll gap="gap-2 lg:gap-4" arrowSize="sm">
                {sortedStandings.map((entry) => {
                    const competitor = entry.competitor;

                    // Skip entries without competitor data
                    if (!competitor) return null;

                    const teamUrl = competitor.fansUnitedTeamId ? nav.team(competitor.fansUnitedTeamId) : "#";

                    return (
                        <TeamCard
                            key={entry.rank}
                            rank={entry.rank}
                            name={competitor.name}
                            shortName={competitor.shortName}
                            logo={competitor.logo}
                            points={entry.stats.points ?? 0}
                            href={teamUrl}
                            played={entry.stats.played}
                            showPlayed={showPlayed}
                            pointsLabel={labels.standings.table.points}
                            playedLabel={labels.standings.table.played}
                        />
                    );
                })}
            </HorizontalScroll>
        </section>
    );
};
